import type { HomeSettingsRW } from "building-types/settings/homeSettings";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import { type SettingsSet } from "building-types/settings/settingsSet";
import type { RWTable, Table } from "common/data/table";
import { ineffectualSettingsFieldsSet } from "../ineffectualSettingsFields";
import { mergeSettingsSet } from "../mergeSettingsSet";
import { entityIsGlobalSettingsSet } from "../settingsTools";
import {
  evaluateActiveSettingsSets,
  flattenSettingsSets,
  settingsSetsUpToSortIndex,
  type FlattenSettingsSetOptions,
} from "./flattenSettingsSets";

/**
 * An object matching the shape of the home settings object but storing settings ids
 * instead of settings data. This is useful for determining which settings are active
 * and which are inherited.
 */

export interface SettingsFragmentProvenance {
  [key: string]: SettingsFieldProvenance;
}

export type SettingsFieldProvenance = string | string[] | SettingsFragmentProvenance | SettingsFragmentProvenance[];

export interface FlattenSettingsSetsWithProvenanceOptions {
  mergedSettingsMaxSortIndex?: number;
}

const ABSENT_CACHE_KEY: object = Object.freeze({});

/**
 * Identity cache: entries stay valid only while the keyed tables keep the same
 * object identity. In-place mutation of a settings fragment without replacing
 * its table will serve stale provenance until the table is replaced (`setIn`).
 */
const homeSettingsProvenanceCache = new WeakMap<
  object,
  WeakMap<object, WeakMap<object, WeakMap<object, WeakMap<object, SettingsFragmentProvenance>>>>
>();

export function getOrCreateWeakMap<K extends object, V extends object>(
  parent: WeakMap<K, V>,
  key: K,
  create: () => V,
): V {
  const existing = parent.get(key);
  if (existing) {
    return existing;
  }
  const created = create();
  parent.set(key, created);
  return created;
}

export function provenanceCacheKeys(props: FlattenSettingsSetOptions): {
  settings: object;
  settingsSets: object;
  optionChoices: object;
  optionSets: object;
  entityDisabledByGroup: object;
} {
  return {
    settings: props.settings,
    settingsSets: props.settingsSets,
    optionChoices: props.optionChoices ?? ABSENT_CACHE_KEY,
    optionSets: props.optionSets ?? ABSENT_CACHE_KEY,
    entityDisabledByGroup: props.entityDisabledByGroup ?? ABSENT_CACHE_KEY,
  };
}

export function getCachedHomeSettingsProvenance(
  props: FlattenSettingsSetOptions,
): SettingsFragmentProvenance | undefined {
  const { settings, settingsSets, optionChoices, optionSets, entityDisabledByGroup } = provenanceCacheKeys(props);
  return homeSettingsProvenanceCache
    .get(settings)
    ?.get(settingsSets)
    ?.get(optionChoices)
    ?.get(optionSets)
    ?.get(entityDisabledByGroup);
}

function setCachedHomeSettingsProvenance(
  props: FlattenSettingsSetOptions,
  provenance: SettingsFragmentProvenance,
): SettingsFragmentProvenance {
  const { settings, settingsSets, optionChoices, optionSets, entityDisabledByGroup } = provenanceCacheKeys(props);
  const bySettingsSets = getOrCreateWeakMap(homeSettingsProvenanceCache, settings, () => new WeakMap());
  const byOptionChoices = getOrCreateWeakMap(bySettingsSets, settingsSets, () => new WeakMap());
  const byOptionSets = getOrCreateWeakMap(byOptionChoices, optionChoices, () => new WeakMap());
  const byDisabled = getOrCreateWeakMap(byOptionSets, optionSets, () => new WeakMap());
  byDisabled.set(entityDisabledByGroup, provenance);
  return provenance;
}

function convertSettingsTableToProvenance(settings: Table<SettingsFragment>): RWTable<SettingsFragmentProvenance> {
  const settingsTableAsProvenance: RWTable<SettingsFragmentProvenance> = {};
  for (const settingsId in settings) {
    settingsTableAsProvenance[settingsId] = convertSettingsFragmentToProvenance(settings[settingsId]);
  }
  return settingsTableAsProvenance;
}

function mergeProvenanceFromSortedSets(
  sortedSets: SettingsSet[],
  settings: FlattenSettingsSetOptions["settings"],
): SettingsFragmentProvenance {
  const settingsProvenance = {} as HomeSettingsRW;
  for (const settingsSet of sortedSets) {
    if (!settingsSet.type || entityIsGlobalSettingsSet(settingsSet)) {
      mergeSettingsSet(settingsProvenance, settingsSet.id, settings);
    }
  }
  return settingsProvenance as unknown as SettingsFragmentProvenance;
}

function getOrComputeHomeSettingsProvenance(props: FlattenSettingsSetOptions): SettingsFragmentProvenance {
  const cached = getCachedHomeSettingsProvenance(props);
  if (cached) {
    return cached;
  }

  const settingsProvenance = mergeProvenanceFromSortedSets(
    evaluateActiveSettingsSets(props),
    convertSettingsTableToProvenance(props.settings) as unknown as FlattenSettingsSetOptions["settings"],
  );
  return setCachedHomeSettingsProvenance(props, settingsProvenance);
}

/**
 * Merges resolved home settings and provenance for inspector use.
 *
 * Merged values follow `flattenSettingsSets`: `entityDisabledByGroup` applies, and
 * `mergedSettingsMaxSortIndex` filters the settings-set table *before* formula eval
 * so later sets cannot throw or run during an earlier-set inspect.
 *
 * Provenance matches `deriveHomeSettingsProvenance` / catalog / settings-src lookups:
 * the full unfiltered stack, without `entityDisabledByGroup`, so inherit tags stay
 * stable. That lookup is identity-cached.
 */
export function flattenSettingsSetsWithProvenance(
  mergedSettings: HomeSettingsRW,
  props: FlattenSettingsSetOptions,
  options?: FlattenSettingsSetsWithProvenanceOptions,
): {
  mergedSettings: HomeSettingsRW;
  activeSettingsSets: RWTable<SettingsSet>;
  settingsProvenance: SettingsFragmentProvenance;
} {
  const mergedSettingsMaxSortIndex = options?.mergedSettingsMaxSortIndex;
  const mergeProps =
    mergedSettingsMaxSortIndex === undefined
      ? props
      : {
          ...props,
          settingsSets: settingsSetsUpToSortIndex(props.settingsSets, mergedSettingsMaxSortIndex),
        };
  const flattened = flattenSettingsSets(mergedSettings, mergeProps);

  const settingsProvenance = deriveHomeSettingsProvenance({
    settings: props.settings,
    settingsSets: props.settingsSets,
    optionChoices: props.optionChoices,
    optionSets: props.optionSets,
  });

  return {
    mergedSettings: flattened.mergedSettings,
    activeSettingsSets: flattened.activeSettingsSets,
    settingsProvenance,
  };
}

export function deriveHomeSettingsProvenance(props: FlattenSettingsSetOptions): SettingsFragmentProvenance {
  return getOrComputeHomeSettingsProvenance(props);
}

export function convertSettingsFragmentToProvenance(obj: SettingsFragment): SettingsFragmentProvenance {
  const result: SettingsFragmentProvenance = {};
  for (const key in obj) {
    // preserve these fields because they are important for merging the settings
    // objects later
    if (ineffectualSettingsFieldsSet.has(key as keyof SettingsFragment)) {
      result[key] = obj[key] as SettingsFieldProvenance;
      continue;
    }

    result[key] = settingsFragmentFieldProvenance(obj.id, obj[key]) as SettingsFieldProvenance;
  }
  return result;
}

function settingsFragmentFieldProvenance(settingsId: string, obj: unknown): SettingsFieldProvenance | null | undefined {
  switch (typeof obj) {
    case "number":
    case "string":
    case "boolean": {
      return settingsId;
    }
    case "object": {
      // we deliberately don't recurse on fields of arrays and objects as that just gets
      // too complicated. thus, the provenance of an array or object is an array or object of
      // settings ids respectively
      if (obj === null) {
        return null;
      } else if (Array.isArray(obj)) {
        return obj.map(() => settingsId);
      } else {
        const result: SettingsFragmentProvenance = {};
        for (const key in obj) {
          result[key] = settingsId;
        }
        return result;
      }
    }
    default:
      return undefined;
  }
}
